import {
  LegacySearchKey,
  SearchEntityType,
  SearchKey,
  SearchParams,
  SearchType,
} from '@adobe/edex/ui/shared/types/search';
import {
  SEARCH_ENTITY_2_TYPE,
  SEARCH_TYPE_2_PARENT,
  SearchFilterAlias,
  ADOBE_EXPRESS,
} from '@adobe/edex/ui/components/search-page/constants';
import { castArray, compact, isArray, isEmpty, keyBy, memoize, uniq } from 'lodash-es';
import { MetadataState } from '@adobe/edex/ui/store/reducers/api/metadata';
import { SearchCanonicalUrlParam } from '@adobe/edex/ui/config/router';
import { AcademicLevelID, SubjectID } from '@adobe/edex/ui/shared/constants';
import { AcademicLevelSchema, SubjectSchema } from '@adobe/edex/ui/shared/types';

export const equalsSearchParentType = (t1: SearchType, t2: SearchType): boolean =>
  SEARCH_TYPE_2_PARENT[t1] === SEARCH_TYPE_2_PARENT[t2];

export const flattenItemsWithChildren = <T extends { children?: T[] }>(items: T[]) =>
  items?.flatMap((item) => [item, ...(item.children || [])]);

export const parseSearchRouteParams = memoize(
  (canonicalUrlParams: Record<string, any>, metadata: MetadataState['byType']): Partial<SearchParams> => {
    const {
      [SearchCanonicalUrlParam.searchType]: searchType,
      [SearchCanonicalUrlParam.searchSubType]: searchSubType,
      [SearchCanonicalUrlParam.filterLabel]: filterLabel,
    } = canonicalUrlParams;
    const isCanonicalUrl = !!searchType;

    if (!isCanonicalUrl) {
      return null;
    }

    const params: Partial<SearchParams> = {
      [SearchKey.type]: searchSubType || searchType,
    };

    if (filterLabel) {
      const isMatch = (candidate: string) => candidate?.toLowerCase() === filterLabel.toLowerCase();

      const alias = Object.values(SearchFilterAlias).find(isMatch);
      if (alias) {
        const getMetadataFilters = (ids: string[], items: (SubjectSchema | AcademicLevelSchema)[]) =>
          items.filter((item) => ids.includes(item.id as SubjectID)).map(({ urlLabel }) => urlLabel);
        const getSubjectFilters = (ids: SubjectID[]) => getMetadataFilters(ids, metadata.subjects);
        const getAcademicLevelFilters = (ids: AcademicLevelID[]) => getMetadataFilters(ids, metadata.academicLevels);
        switch (alias) {
          case SearchFilterAlias.humanities:
            params[SearchKey.subject] = getSubjectFilters([SubjectID.humanities]);
            break;
          case SearchFilterAlias.stem:
            params[SearchKey.subject] = getSubjectFilters([SubjectID.math, SubjectID.science]);
            break;
          case SearchFilterAlias.socialSciences:
            params[SearchKey.subject] = getSubjectFilters([SubjectID.socialSciences]);
            break;
          case SearchFilterAlias.digitalMediaArt:
            params[SearchKey.subject] = getSubjectFilters([SubjectID.digitalMedia, SubjectID.arts]);
            break;
          case SearchFilterAlias.higherEd:
            params[SearchKey.ageLevel] = getAcademicLevelFilters([AcademicLevelID.higherED]);
            break;
        }
      } else {
        // match product
        const product = metadata.products.find((item) => isMatch(item.urlLabel));
        if (product) {
          params[SearchKey.product] = product.urlLabel;
        }

        // match subject
        const subject = flattenItemsWithChildren(metadata.subjects).find((item) => isMatch(item.urlLabel));
        if (subject) {
          params[SearchKey.subject] = subject.urlLabel;
        }

        // match age level
        const academicLevel = metadata.academicLevels.find((item) => isMatch(item.urlLabel));
        if (academicLevel) {
          params[SearchKey.ageLevel] = academicLevel.urlLabel;
        }
      }
    }

    return params;
  }
);

export function normalizeSelectedMetadataItems(
  {
    selected,
    metadata,
    fromKey,
    toKey,
  }: {
    selected: string[];
    metadata: any[];
    fromKey: string;
    toKey: string;
  },
  includeParent = true
): string[] {
  const all = flattenItemsWithChildren(metadata);
  const byID = keyBy(all, 'id');

  // items that match selected "fromKey" ids
  const selectedItems = all.filter((item) => selected.includes(item[fromKey]));

  /**
   * Below is parent/child relation adjustment
   * TODO: consider making these adjustments on BE when marshalling to ES (and remove client side part after it's done)
   */
  // include parents (if child) of the selected items
  const includeParents = includeParent ? compact(selectedItems.map((item) => byID[item.parentID])) : [];
  // include children (if parent) of the selected items (currently applies to subjects only)
  const includeChildren = selectedItems.flatMap((item) => item.children || []);
  // when parent item is selected - check for other parents with shared children
  const includeCommonParents = all.filter((item) =>
    item.children?.some((c) => includeChildren.find((ic) => ic[fromKey] === c[fromKey]))
  );
  /**
   * End of adjustment
   */

  // finally map items to desired id
  const ids = [
    ...selectedItems,
    // include adjustments
    ...includeParents,
    ...includeChildren,
    ...includeCommonParents,
  ].map((item) => item[toKey]);
  return uniq(compact(ids));
}

const convertMetadataKeys = (values: string | string[], metadata: any[], fromKey: string, toKey: string) =>
  castArray(values)
    .map((value) => metadata?.find((item) => item[fromKey]?.toLowerCase() === value?.toLowerCase())?.[toKey])
    .filter(Boolean);

/**
 * Convert legacy query params for graceful /browse->/search redirects
 */
export const normalizeLegacySearchQueryParams = memoize(
  (legacyParams: Record<string, any>, metadata: MetadataState['byType']) => {
    const {
      [LegacySearchKey.type]: searchEntityType,
      [LegacySearchKey.product]: products,
      [LegacySearchKey.subject]: subjects,
      [LegacySearchKey.ageLevel]: ageLevels,
      ...params
    } = legacyParams;

    params[SearchKey.type] =
      SEARCH_ENTITY_2_TYPE.get(searchEntityType as SearchEntityType) || SearchType.teachingResources;

    if (products) {
      params[SearchKey.product] = convertMetadataKeys(products, metadata.products, 'title', 'urlLabel');
    }

    if (subjects) {
      params[SearchKey.subject] = convertMetadataKeys(
        subjects,
        flattenItemsWithChildren(metadata.subjects),
        'i18nLabel',
        'urlLabel'
      );
    }

    if (ageLevels) {
      params[SearchKey.ageLevel] = convertMetadataKeys(ageLevels, metadata.academicLevels, 'i18nLabel', 'urlLabel');
    }

    return params;
  }
);

export const searchRedirects = [
  {
    regex: /product?=spark/i,
    redirectedURL: 'product=creative-cloud-express',
  },
];

export const isSearchParamChanged = (queryParamsOld, queryParamsNew, searchKey: SearchKey) =>
  queryParamsNew[searchKey] !== queryParamsOld[searchKey];

export const isExpressProduct = (product) => {
  if (isEmpty(product)) {
    return false;
  }
  if (isArray(product)) {
    if (product.indexOf(ADOBE_EXPRESS) <= -1) {
      return false;
    }
  } else {
    if (product !== ADOBE_EXPRESS) {
      return false;
    }
  }
  return true;
};
